import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '../shell/AppShell';
import { RequireAuth } from '../guards/RequireAuth';
import { RequireRole } from '../guards/RequireRole';
import { RequirePasswordSetup } from '../guards/RequirePasswordSetup';
import { LoginPage } from '../routes/LoginPage';
import { SetPasswordPage } from '../routes/SetPasswordPage';
import { NotFoundPage } from '../routes/NotFoundPage';
import { APP_ROLES, COMPANY_SCOPE_ROLES } from '../types/auth';

// Route-level code splitting (carried shared improvement): each feature page is lazy-loaded
// so the initial bundle stays lean; AppShell renders a Suspense fallback while chunks load.
// Named exports are adapted to default for React.lazy.
const lazyPage = <T extends string>(loader: () => Promise<Record<T, React.ComponentType>>, name: T) =>
  lazy(() => loader().then((m) => ({ default: m[name] })));

const DebtorSummaryPage = lazyPage(() => import('../features/debtors/DebtorSummaryPage'), 'DebtorSummaryPage');
const CustomerStatementPage = lazyPage(() => import('../features/debtors/CustomerStatementPage'), 'CustomerStatementPage');
const OpenDocumentsPage = lazyPage(() => import('../features/debtors/OpenDocumentsPage'), 'OpenDocumentsPage');
const AgingPage = lazyPage(() => import('../features/reports/AgingPage'), 'AgingPage');
const PaymentsPage = lazyPage(() => import('../features/reports/PaymentsPage'), 'PaymentsPage');
const AdvancesPage = lazyPage(() => import('../features/reports/AdvancesPage'), 'AdvancesPage');
const ExceptionsPage = lazyPage(() => import('../features/reports/ExceptionsPage'), 'ExceptionsPage');
const ManagerSummaryPage = lazyPage(() => import('../features/reports/ManagerSummaryPage'), 'ManagerSummaryPage');
const AlertsPage = lazyPage(() => import('../features/alerts/AlertsPage'), 'AlertsPage');
const CustomersPage = lazyPage(() => import('../features/customers/CustomersPage'), 'CustomersPage');
const OpeningBalancesPage = lazyPage(() => import('../features/opening/OpeningBalancesPage'), 'OpeningBalancesPage');
const ImportWizardPage = lazyPage(() => import('../features/import/ImportWizardPage'), 'ImportWizardPage');
const ImportHistoryPage = lazyPage(() => import('../features/import/ImportHistoryPage'), 'ImportHistoryPage');
const CommissionDashboardPage = lazyPage(() => import('../features/commission/CommissionDashboardPage'), 'CommissionDashboardPage');
const AnalyticsPage = lazyPage(() => import('../features/commission/AnalyticsPage'), 'AnalyticsPage');
const RecipientsPage = lazyPage(() => import('../features/commission/RecipientsPage'), 'RecipientsPage');
const RulesPage = lazyPage(() => import('../features/commission/RulesPage'), 'RulesPage');
const EventsPage = lazyPage(() => import('../features/commission/EventsPage'), 'EventsPage');
const AdjustmentsPage = lazyPage(() => import('../features/commission/AdjustmentsPage'), 'AdjustmentsPage');
const OverridesPage = lazyPage(() => import('../features/commission/OverridesPage'), 'OverridesPage');
const BackfillPage = lazyPage(() => import('../features/commission/BackfillPage'), 'BackfillPage');
const MonthlyPage = lazyPage(() => import('../features/commission/MonthlyPage'), 'MonthlyPage');
const SettlementPage = lazyPage(() => import('../features/commission/SettlementPage'), 'SettlementPage');
const CompanySettingsPage = lazyPage(() => import('../features/admin/CompanySettingsPage'), 'CompanySettingsPage');
const UsersPage = lazyPage(() => import('../features/admin/UsersPage'), 'UsersPage');
const CompaniesPage = lazyPage(() => import('../features/admin/CompaniesPage'), 'CompaniesPage');
const RolesPage = lazyPage(() => import('../features/admin/RolesPage'), 'RolesPage');
const AuditPage = lazyPage(() => import('../features/admin/AuditPage'), 'AuditPage');
const CalendarPage = lazyPage(() => import('../features/admin/CalendarPage'), 'CalendarPage');
const PoliciesPage = lazyPage(() => import('../features/admin/PoliciesPage'), 'PoliciesPage');
const TemplatesPage = lazyPage(() => import('../features/admin/TemplatesPage'), 'TemplatesPage');
const PlansPage = lazyPage(() => import('../features/admin/PlansPage'), 'PlansPage');
const PaymentMatchingPage = lazyPage(() => import('../features/admin/PaymentMatchingPage'), 'PaymentMatchingPage');
const RefundsPage = lazyPage(() => import('../features/refunds/RefundsPage'), 'RefundsPage');
const PromisesPage = lazyPage(() => import('../features/communication/PromisesPage'), 'PromisesPage');
const TasksPage = lazyPage(() => import('../features/tasks/TasksPage'), 'TasksPage');
const NotebookPage = lazyPage(() => import('../features/notebook/NotebookPage'), 'NotebookPage');

// Commission scope: fn_commission_authorize grants company scope to admin/finance_manager/
// management and a self-service (D4, own-recipient) scope to sales_manager. Dashboard,
// analytics AND settlement are D4-safe (auto-scoped — settlement since migration 0097/D-155
// put the same own-recipient filter on the two settlement SELECT policies); recipients/rules/
// events/adjustments/overrides are company-config, restricted to the company-scope roles.
const COMMISSION_COMPANY_ROLES = ['administrator', 'finance_manager', 'management'] as const;
const COMMISSION_SELF_SERVICE_ROLES = ['administrator', 'finance_manager', 'management', 'sales_manager'] as const;
// Slice P0-H (PO): the manager-summary and exceptions screens are cross-manager review surfaces
// — a sales_manager comparing themselves against their peers is not what either exists for. This
// is a PRODUCT decision, not a backend mirror: fn_report_authorize (0020) would happily serve a
// sales_manager their own scoped slice. COMPANY_SCOPE_ROLES is reused verbatim (types/auth.ts)
// rather than restated, so "everyone except sales_manager" has exactly one definition. Route
// guard and Sidebar visibility (COMPANY_REPORTS in Sidebar.tsx) must stay in agreement — pinned
// by navGuardParity.test.tsx.
const COMPANY_REPORT_ROLES = COMPANY_SCOPE_ROLES;
// Slice 5A: Administration is administrator-only (ADMINISTRATION-DESIGN §9). Route guard and
// Sidebar visibility (see ADMIN_ROLES in Sidebar.tsx) must stay in agreement.
const ADMIN_ROLES = ['administrator'] as const;
// /admin/companies is not administrator-only as a whole: 0135 §3 widened
// fn_admin_set_member_status to administrator|management, and that page is where deactivation
// lives. The provisioning sections inside it gate themselves on the administrator role; a
// management user sees only the member list. Guard is UI-only either way — every RPC on the page
// re-derives authority server-side.
const PLATFORM_ADMIN_ROLES = ['administrator', 'management'] as const;
// Import Gateway backend (fn_import_authorize, 0072/0073) admits only administrator|finance_manager —
// the route guard mirrors that exactly (D-082): showing the wizard/history to a role the server will
// always 403 reads as a broken app, not a permission boundary. Route guard and Sidebar visibility
// (see IMPORT_ROLES in Sidebar.tsx) must stay in agreement. RLS/RPC authority is unchanged and remains
// the real enforcement — this is UI-visibility only.
const IMPORT_ROLES = ['administrator', 'finance_manager'] as const;
// fn_payment_authorize_write (0082/0083) admits only administrator|finance_manager — the route
// guard mirrors that exactly. Shared by payment-policies (7.5-UI), installment-templates
// (7.6-UI, fn_installment_template_create/_rename/fn_resolve_installment_amounts, 0086) and
// recovery-plans (7.7-B, fn_recovery_plan_create/_activate/_cancel/_complete/_supersede,
// migrations 0087/0088) and confirmed payment matching (7.8-B, fn_recovery_plan_match_propose/
// _confirm/_reverse, migrations 0089/0090) — same underlying authorize function, D-J. Route
// guard and Sidebar visibility (see PAYMENT_POLICY_ONLY in Sidebar.tsx) must stay in agreement.
const PAYMENT_POLICY_ROLES = ['administrator', 'finance_manager'] as const;
// Slice P1-A: fn_customer_create (0098) reuses fn_payment_authorize_write verbatim, so the
// customer registry admits exactly its role set. A separate guard group — NOT a new entry in
// the payment-policy group — so the structural sibling-set tests that pin that group stay
// meaningful. Route guard and Sidebar visibility (CUSTOMER_ENTRY in navModel.ts) must stay in
// agreement — pinned by navGuardParity.test.tsx.
const CUSTOMER_ROLES = ['administrator', 'finance_manager'] as const;
// Slice P3: the opening-balance RPCs (0102) derive authority from fn_payment_authorize_write —
// exactly this role set. A separate guard group (the CUSTOMER_ROLES precedent) so the
// structural sibling-set tests pinning other groups stay meaningful. Route guard and Sidebar
// visibility (OPENING_ENTRY in navModel.ts) must stay in agreement — navGuardParity.test.tsx.
const OPENING_ROLES = ['administrator', 'finance_manager'] as const;
// D-210: the refunds history — financial audit data, company-wide. fn_refund_list (0139) and the
// execute_refund Edge action both authorize through fn_payment_authorize_write, so exactly this set.
// Its own guard group (the CUSTOMER_ROLES/OPENING_ROLES precedent) so the sibling-set tests pinning
// other groups stay meaningful. Route guard and Sidebar visibility (REFUND_ENTRY in navModel.ts)
// must stay in agreement — navGuardParity.test.tsx.
const REFUND_ROLES = ['administrator', 'finance_manager'] as const;
// D-223: the Management Notebook (collection goals) — exactly fn_notebook_authorize_write's set
// (0146): administrator | management, nobody else (A9 — a sales_manager does not see it at all).
// Same members as PLATFORM_ADMIN_ROLES but its own group, so each group keeps one meaning and the
// sibling-set tests stay meaningful. Route guard and Sidebar visibility (NOTEBOOK_ENTRY in
// navModel.ts) must stay in agreement — navGuardParity.test.tsx.
const NOTEBOOK_ROLES = ['administrator', 'management'] as const;

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: <RequireAuth />,
    children: [
      // Outside the gate below — the one destination a user with a pending password setup may
      // reach. Gating it too would be a redirect loop, and it renders outside AppShell because
      // there is no navigation to offer someone who cannot yet enter the app.
      { path: '/set-password', element: <SetPasswordPage /> },
      {
        element: <RequirePasswordSetup />,
        children: [
          {
            element: <AppShell />,
            children: [
              // Slice W1-A — the landing screen answers "what should I do today?", which is the
              // alerts/tasks queue (7.11-B), not "who owes me?" (/debtors). Safe as a default for
              // EVERY authenticated role because /alerts sits in the APP_ROLES guard group below —
              // the same group as /debtors was — so no role can land on a denied screen.
              { index: true, element: <Navigate to="/alerts" replace /> },
              {
                element: <RequireRole allow={APP_ROLES} />,
                children: [
                  { path: 'debtors', element: <DebtorSummaryPage /> },
                  { path: 'debtors/:customerId', element: <CustomerStatementPage /> },
                  { path: 'debtors/:customerId/open-documents', element: <OpenDocumentsPage /> },
                  { path: 'aging', element: <AgingPage /> },
                  { path: 'payments', element: <PaymentsPage /> },
                  { path: 'advances', element: <AdvancesPage /> },
                  // Phase 7 Slice 7.11-B (D-AE): fn_alert_task_list/_refresh (0094) reuse the SAME
                  // fn_report_authorize scope as every other route in this group — sales_manager
                  // (own customers) vs. company scope (administrator/finance_manager/management/
                  // finance_operator) — unlike the payment-policy-authority routes below, which admit
                  // only administrator|finance_manager. APP_ROLES is the correct, not a new, group.
                  { path: 'alerts', element: <AlertsPage /> },
                  // D-218: fn_promise_summary (0144) scopes through fn_engagement_authorize +
                  // fn_report_scope_customers — the same rule as this whole group (sales_manager own
                  // customers, every other role company-wide), so it joins APP_ROLES, not a new group.
                  { path: 'promises', element: <PromisesPage /> },
                  // D-219: fn_internal_task_list (0145) admits every APP_ROLES role through
                  // fn_engagement_authorize and scopes a sales_manager to their own assigned tasks
                  // itself; create / close / return are refused server-side for everyone but
                  // administrator | management — so this joins APP_ROLES, not a new group.
                  { path: 'tasks', element: <TasksPage /> },
                ],
              },
              {
                element: <RequireRole allow={COMPANY_REPORT_ROLES} />,
                children: [
                  { path: 'exceptions', element: <ExceptionsPage /> },
                  { path: 'managers', element: <ManagerSummaryPage /> },
                ],
              },
              {
                element: <RequireRole allow={CUSTOMER_ROLES} />,
                children: [{ path: 'customers', element: <CustomersPage /> }],
              },
              {
                element: <RequireRole allow={OPENING_ROLES} />,
                children: [{ path: 'opening-balances', element: <OpeningBalancesPage /> }],
              },
              {
                element: <RequireRole allow={REFUND_ROLES} />,
                children: [{ path: 'refunds', element: <RefundsPage /> }],
              },
              {
                element: <RequireRole allow={NOTEBOOK_ROLES} />,
                children: [{ path: 'notebook', element: <NotebookPage /> }],
              },
              {
                element: <RequireRole allow={IMPORT_ROLES} />,
                children: [
                  { path: 'import', element: <ImportWizardPage /> },
                  { path: 'import/history', element: <ImportHistoryPage /> },
                ],
              },
              {
                element: <RequireRole allow={COMMISSION_SELF_SERVICE_ROLES} />,
                children: [
                  { path: 'commission', element: <CommissionDashboardPage /> },
                  { path: 'commission/analytics', element: <AnalyticsPage /> },
                  // D-155: RLS (0097) narrows the settlement read to the caller's own recipient,
                  // so a sales_manager opening this page sees their own settlement and nothing
                  // else. Writes stay closed — fn_commission_transition_batch →
                  // fn_commission_authorize_write (0040) admits administrator|finance_manager
                  // only, and SettlementPage hides the transition controls for everyone else.
                  { path: 'commission/settlement', element: <SettlementPage /> },
                  // K-M (D-184): information only — the RPC decides scope itself, so a
                  // sales_manager opening this sees their own figures and nothing else.
                  { path: 'commission/monthly', element: <MonthlyPage /> },
                ],
              },
              {
                element: <RequireRole allow={COMMISSION_COMPANY_ROLES} />,
                children: [
                  { path: 'commission/recipients', element: <RecipientsPage /> },
                  { path: 'commission/rules', element: <RulesPage /> },
                  { path: 'commission/events', element: <EventsPage /> },
                  { path: 'commission/adjustments', element: <AdjustmentsPage /> },
                  { path: 'commission/overrides', element: <OverridesPage /> },
                ],
              },
              {
                // K-D-UI (D-183): the retroactive backfill is administrator-only, matching
                // fn_commission_backfill_authorize (0109:88) — stricter than the rest of
                // /commission/*, so it gets its own ADMIN_ROLES block rather than joining the
                // COMMISSION_COMPANY_ROLES group above.
                element: <RequireRole allow={ADMIN_ROLES} />,
                children: [{ path: 'commission/backfill', element: <BackfillPage /> }],
              },
              {
                element: <RequireRole allow={ADMIN_ROLES} />,
                children: [
                  { path: 'admin', element: <CompanySettingsPage /> },
                  { path: 'admin/users', element: <UsersPage /> },
                  { path: 'admin/roles', element: <RolesPage /> },
                  { path: 'admin/audit', element: <AuditPage /> },
                  { path: 'admin/calendar', element: <CalendarPage /> },
                ],
              },
              {
                element: <RequireRole allow={PLATFORM_ADMIN_ROLES} />,
                children: [{ path: 'admin/companies', element: <CompaniesPage /> }],
              },
              {
                element: <RequireRole allow={PAYMENT_POLICY_ROLES} />,
                children: [
                  { path: 'admin/payment-policies', element: <PoliciesPage /> },
                  { path: 'admin/installment-templates', element: <TemplatesPage /> },
                  { path: 'admin/recovery-plans', element: <PlansPage /> },
                  { path: 'admin/payment-matching', element: <PaymentMatchingPage /> },
                ],
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
